import AddIcon from '@mui/icons-material/Add';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { DataGrid, type GridColDef } from '@mui/x-data-grid';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { contentStringApi, contentStringKeys } from '@/api/skin/contentStringApi';
import { type AppType } from '@/models/enums/AppType';
import { appTypeLabel } from '@/models/media-network/appLabels';
import {
    type ContentStringCategory, type ContentStringKey, FALLBACK_LOCALE, parseLocaleValues,
} from '@/models/skin/ContentString';
import { useLocalizedStrings } from '../../localization/LocalizedStringsProvider';
import { Perm } from '../../permissions/permissions';
import { usePermissions } from '../../permissions/usePermissions';
import { fillHeight } from '../../theme';
import DataGridDeleteButton from '../common/data-grid/DataGridDeleteButton';
import DataGridEditButton from '../common/data-grid/DataGridEditButton';
import { useToast } from '../common/toast/ToastProvider';
import ContentStringCategoryDialog from './dialogs/ContentStringCategoryDialog';
import ContentStringKeyDialog from './dialogs/ContentStringKeyDialog';

/**
 * The key catalogue: what strings exist, what they default to, and which apps use them.
 *
 * Super-admin only, and deliberately separate from Configuration > Content Strings. A key exists because our
 * apps reference it in code, so renaming or deleting one breaks every customer at once - that is platform
 * data wearing a customer-facing screen, not a customer's own content.
 */
const ContentStringCatalogue = () => {
    const strings = useLocalizedStrings();
    const perms = usePermissions();
    const canCreate = perms.canCreate(Perm.adminConfiguration);
    const canUpdate = perms.canUpdate(Perm.adminConfiguration);
    const canDelete = perms.canDelete(Perm.adminConfiguration);
    const toast = useToast();
    const qc = useQueryClient();

    const [categoryId, setCategoryId] = useState('');
    const [search, setSearch] = useState('');
    const [keyDialogOpen, setKeyDialogOpen] = useState(false);
    const [editingKey, setEditingKey] = useState<ContentStringKey | null>(null);
    const [categoryDialogOpen, setCategoryDialogOpen] = useState(false);

    const { data: categories } = useQuery<ContentStringCategory[]>({
        queryKey: contentStringKeys.categories(),
        queryFn: () => contentStringApi.listCategories(),
    });

    const { data: keys, isPending } = useQuery<ContentStringKey[]>({
        queryKey: contentStringKeys.keys(),
        queryFn: () => contentStringApi.listKeys(),
        staleTime: 0,
    });

    const { mutateAsync: removeKey } = useMutation({
        mutationFn: (id: string) => contentStringApi.deleteKey(id),
        onSuccess: async () => {
            toast({ severity: 'success', message: strings.contentStringKeyDeleted });
            await qc.invalidateQueries({ queryKey: contentStringKeys.keys() });
        },
        onError: (e: unknown) => {
            // A 409 here means brands still override the key, and the server says how many. That is the whole
            // answer - a generic "could not delete" would send someone hunting for a cause we already know.
            const body = (e as { body?: string })?.body;
            toast({
                severity: 'error',
                message: typeof body === 'string' && body ? body : strings.contentStringKeyDeleteError,
            });
        },
    });

    const filtered = useMemo(() => {
        const term = search.trim().toLowerCase();
        return (keys ?? []).filter(k => {
            if (categoryId && k.categoryId !== categoryId) return false;
            if (term && !k.key.toLowerCase().includes(term)) return false;
            return true;
        });
    }, [keys, categoryId, search]);

    const columns: GridColDef<ContentStringKey>[] = [
        { field: 'key', headerName: strings.contentStringKey, flex: 3 },
        {
            field: 'categoryName',
            headerName: strings.category,
            flex: 1,
            renderCell: p => <Chip size="small" label={p.row.categoryName} />,
        },
        {
            field: 'appTypes',
            headerName: strings.contentStringApps,
            flex: 2,
            sortable: false,
            renderCell: p => (
                <Stack direction="row" spacing={0.5} sx={{ height: '100%', alignItems: 'center', flexWrap: 'nowrap', overflow: 'hidden' }}>
                    {p.row.appTypes.map(a => (
                        <Chip key={a} size="small" variant="outlined" label={appTypeLabel(a as AppType)} />
                    ))}
                </Stack>
            ),
        },
        {
            field: 'defaultStringJson',
            headerName: strings.contentStringDefaults,
            flex: 3,
            sortable: false,
            renderCell: p => {
                const parsed = parseLocaleValues(p.row.defaultStringJson);
                const english = parsed[FALLBACK_LOCALE.toLowerCase()];
                const others = Object.keys(parsed).filter(l => l !== FALLBACK_LOCALE.toLowerCase());

                // No en-US is the one state worth shouting about: the fallback chain ends there, so such a key
                // resolves to its own name for every brand in every language.
                if (!english) {
                    return (
                        <Tooltip title={strings.contentStringNoEnglishWarning}>
                            <Stack direction="row" spacing={0.5} alignItems="center" sx={{ height: '100%' }}>
                                <WarningAmberIcon color="error" fontSize="small" />
                                <Typography variant="body2" color="error.main">{strings.contentStringNoEnglish}</Typography>
                            </Stack>
                        </Tooltip>
                    );
                }

                return (
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ height: '100%', minWidth: 0 }}>
                        <Typography variant="body2" noWrap>{english}</Typography>
                        {others.length > 0 && (
                            <Chip size="small" variant="outlined" label={`+${others.length}`} />
                        )}
                    </Stack>
                );
            },
        },
        {
            field: 'actions',
            headerName: '',
            width: 100,
            sortable: false,
            renderCell: p => (
                <Stack direction="row">
                    <DataGridEditButton
                        id={p.row.id}
                        onClick={() => { setEditingKey(p.row); setKeyDialogOpen(true); }}
                        readOnly={!canUpdate}
                    />
                    <DataGridDeleteButton
                        id={p.row.id}
                        warningMessage={strings.contentStringDeleteKeyWarning}
                        onClick={id => removeKey(id)}
                        hidden={!canDelete}
                    />
                </Stack>
            ),
        },
    ];

    return (
        <Box sx={{ ...fillHeight, overflow: 'hidden', display: 'flex', flexDirection: 'column', p: 2 }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
                <Typography variant="h6">{strings.contentStringCatalogue}</Typography>
                <Stack direction="row" spacing={1}>
                    {canUpdate && <Button onClick={() => setCategoryDialogOpen(true)}>{strings.contentStringManageCategories}</Button>}
                    {canCreate &&
                    <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        disabled={(categories ?? []).length === 0}
                        onClick={() => { setEditingKey(null); setKeyDialogOpen(true); }}
                    >
                        {strings.contentStringNewKey}
                    </Button>
                    }
                </Stack>
            </Stack>

            <Stack direction="row" spacing={2} sx={{ mb: 2, flexWrap: 'wrap' }}>
                <TextField
                    select
                    size="small"
                    label={strings.category}
                    value={categoryId}
                    onChange={e => setCategoryId(e.target.value)}
                    sx={{ minWidth: 200 }}
                >
                    <MenuItem value="">{strings.all}</MenuItem>
                    {(categories ?? []).map(c => <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>)}
                </TextField>

                <TextField
                    size="small"
                    label={strings.search}
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                    sx={{ minWidth: 240 }}
                />
            </Stack>

            <DataGrid
                rows={filtered}
                columns={columns}
                loading={isPending}
                disableRowSelectionOnClick
                initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
                pageSizeOptions={[25, 50, 100]}
            />

            {keyDialogOpen && (
                <ContentStringKeyDialog
                    open
                    editing={editingKey}
                    categories={categories ?? []}
                    readOnly={!!editingKey && !canUpdate}
                    onClose={() => { setKeyDialogOpen(false); setEditingKey(null); }}
                />
            )}

            {categoryDialogOpen && (
                <ContentStringCategoryDialog
                    open
                    categories={categories ?? []}
                    onClose={() => setCategoryDialogOpen(false)}
                />
            )}
        </Box>
    );
};

export default ContentStringCatalogue;
